import React, { useState } from "react";
import { Box, useTheme, CircularProgress, Typography, Snackbar, Alert, useMediaQuery } from "@mui/material";
import WorkflowNavbar from "../../components/Workflow/WorkflowNavbar";
import ActionsTab from "../../components/Workflow/ActionsTab";
import AnalyticsTab from "../../components/Workflow/AnalyticsTab";
import RunsTab from "../../components/Workflow/RunsTab";
import RevisionsTab from "../../components/Workflow/RevisionsTab";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useEffect } from "react";
import { setBreadcrumbs } from "../../redux/Slice/Dashboard/leftNavSlice";
import { setCurrentPage } from '../../redux/Slice/Dashboard/leftNavSlice';
import { fetchWorkflowById, fetchTriggers, fetchActions } from '../../redux/Slice/Workflow/workflowSlice';
import { fetchUserPermissions } from "../../redux/Slice/User/userDetailsSlice";
import AuthView from "../../utils/AuthView";

const TAB_COMPONENTS = {
  Actions: ActionsTab,
  Analytics: AnalyticsTab,
  Runs: RunsTab,
  Revisions: RevisionsTab,
};



const WorkflowCreate = () => {
  const theme = useTheme();
  /** xs + sm: flex-fill Home outlet above BottomNav (matches Workflow list / Contacts). md+ unchanged. */
  const isXsSmOutlet = useMediaQuery(theme.breakpoints.down("md"));
  const isDark = theme.palette.mode === "dark";
  const [isWorkflowOn, setIsWorkflowOn] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { accountid, workflowId, tab } = useParams();
  const dispatch = useDispatch();
  const { selectedWorkflow, loading, triggers: triggersRaw, actions: actionsRaw } = useSelector(state => state.workflow);
  const WorkflowPermissions = useSelector(
    (state) => state.userDetails.userPermissions?.permissions?.Workflow || {}
  );
  const triggers = Array.isArray(triggersRaw) ? triggersRaw : [];
  const actions = Array.isArray(actionsRaw) ? actionsRaw : [];
  const workflowName = selectedWorkflow?.currentVersion?.name || selectedWorkflow?.name || "";
  const tabList = ["Actions", "Analytics", "Runs", "Revisions"];
  // Set initial tab from URL or default to Actions
  const initialTab = tabList.find(t => t.toLowerCase() === (tab || '').toLowerCase()) || "Actions";
  const [activeTab, setActiveTab] = useState(initialTab);
  const handleToggleWorkflow = async () => {
    try {
      const newStatus = isWorkflowOn ? "off" : "on";
  
      await fetch(`/api/workflow/turnOnOff/${workflowId}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({ status: newStatus })
      }).then(async res => {
        if (!res.ok) {
          const data = await res.json();
          throw new Error(data.message || "Failed to toggle workflow");
        }
      });
  
      setIsWorkflowOn(prev => !prev);
  
    } catch (err) {
      setToggleErrorSnackbar({
        open: true,
        message: err.message || "Failed to toggle workflow"
      });
    }
  };
  const [toggleErrorSnackbar, setToggleErrorSnackbar] = useState({ open: false, message: "" });
  // When tab changes, update the URL (preserve ?channel= for list back-navigation)
  useEffect(() => {
    if (tab !== activeTab.toLowerCase()) {
      navigate({
        pathname: `/home/${accountid}/workflow/${workflowId}/create/${activeTab.toLowerCase()}`,
        search: location.search,
      });
    }
  }, [activeTab, tab, accountid, workflowId, navigate, location.search]);
  useEffect(() => {
    dispatch(fetchUserPermissions());
    dispatch(setCurrentPage('Workflow'));
    if (workflowName) {
      dispatch(setBreadcrumbs(["Workflow", workflowName]));
    } else {
      dispatch(setBreadcrumbs(["Workflow"]));
    }
  }, [workflowName, dispatch]);
  // Fetch workflow by id if needed
  useEffect(() => {
    if (workflowId && (!selectedWorkflow || (selectedWorkflow._id !== workflowId && selectedWorkflow.id !== workflowId))) {
      dispatch(fetchWorkflowById(workflowId));
    }
  }, [workflowId, selectedWorkflow, dispatch]);
  // Fetch triggers and actions if not loaded
  useEffect(() => {
    if (triggers.length === 0) {
      dispatch(fetchTriggers());
    }
    if (actions.length === 0) {
      dispatch(fetchActions());
    }
  }, [triggers.length, actions.length, dispatch]);
  // When initializing a new workflow, ensure currentVersion.branches = { start: [] }
  useEffect(() => {
    if (workflowId && !selectedWorkflow) {
      dispatch(fetchWorkflowById(workflowId));
    }
    if (selectedWorkflow && selectedWorkflow._id === workflowId && !selectedWorkflow.currentVersion) {
      dispatch(fetchWorkflowById(workflowId));
    }
    if (selectedWorkflow && selectedWorkflow._id === workflowId && selectedWorkflow.currentVersion && !selectedWorkflow.currentVersion.branches) {
      dispatch(fetchWorkflowById(workflowId));
    }
  }, [workflowId, selectedWorkflow, dispatch]);

  // Check if user has permission to view workflows
  if (WorkflowPermissions.View === false) {
    return <AuthView />;
  }

  if (loading) return (
    <Box sx={{
      ...(isXsSmOutlet
        ? {
            height: "100%",
            width: "100%",
            flex: 1,
            minHeight: 0,
            alignSelf: "stretch",
            boxSizing: "border-box",
          }
        : {
            height: "100%",
          }),
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      bgcolor: isDark ? '#181818' : '#f5f5f5',
      color: isDark ? '#fff' : '#222',
    }}>
      <CircularProgress sx={{ color: isDark ? '#fff' : 'primary.main', mb: 3 }} />
      <Typography variant="h6" sx={{ color: isDark ? '#fff' : '#222', fontWeight: 600 }}>
        Loading workflow...
      </Typography>
    </Box>
  );

  const ActiveComponent = TAB_COMPONENTS[activeTab] || ActionsTab;
  return (
    <Box
      sx={{
        /** Fill Home outlet (below TopNav) on all breakpoints — avoid 100vh so tabs don’t overflow and hide pagination */
        flex: 1,
        minHeight: 0,
        height: "100%",
        width: "100%",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        boxSizing: "border-box",
      }}
    >
    <Box
      sx={{
        overflow: "hidden",
        p: 0,
        bgcolor: isDark ? "#181818" : "#f5f5f5",
        flex: 1,
        minHeight: 0,
        height: "100%",
        width: "100%",
        display: "flex",
        flexDirection: "column",
        boxSizing: "border-box",
      }}
    >
      <WorkflowNavbar
  activeTab={activeTab}
  onTabChange={setActiveTab}
  tabs={tabList}
  isWorkflowOn={isWorkflowOn}
  onToggleWorkflow={handleToggleWorkflow}   // ✅ CORRECT
  onToggleError={(msg) =>
    setToggleErrorSnackbar({
      open: true,
      message: msg || "Failed to toggle workflow"
    })
  }
  onGoBack={() => {
    const params = new URLSearchParams(location.search);
    const q = params.get("channel");
    const fromQuery = q === "instagram" || q === "facebook" || q === "website" || q === "qr" || q === "whatsapp_web" ? q : null;
    const fromWorkflow =
      selectedWorkflow?.channel === "instagram" || selectedWorkflow?.channel === "facebook" || selectedWorkflow?.channel === "website" || selectedWorkflow?.channel === "qr" || selectedWorkflow?.channel === "whatsapp_web"
        ? selectedWorkflow.channel
        : null;
    const ch = fromQuery || fromWorkflow;
    navigate(`/home/${accountid}/workflow${ch ? `?channel=${ch}` : ""}`);
  }}
/>

  
      <Box
        sx={{
          width: "100%",
          flex: 1,
          minHeight: 0,
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <ActiveComponent workflowId={workflowId} />
      </Box>
    </Box>

    <Snackbar
      open={toggleErrorSnackbar.open}
      autoHideDuration={5000}
      onClose={() =>
        setToggleErrorSnackbar(s => ({ ...s, open: false }))
      }
      sx={{
        zIndex: 99999,
      }}
    >
      <Alert
        severity="error"
        onClose={() =>
          setToggleErrorSnackbar(s => ({ ...s, open: false }))
        }
        sx={{ width: "100%" }}
      >
        {toggleErrorSnackbar.message}
      </Alert>
    </Snackbar>
    </Box>
  );
};



export default WorkflowCreate; 